import { supabase } from '../lib/supabase';

// Types pour les nouvelles fonctionnalités
export interface StudentLearningStats {
  id: string;
  student_id: string;
  enrollment_id: string;
  lesson_id?: string;
  module_id?: string;
  course_id: string;
  session_start: string;
  session_end?: string;
  time_spent_minutes: number;
  progress_before: number;
  progress_after: number;
  actions_count: number;
  tenant_id: number;
  created_at: string;
  updated_at: string;
}

export interface StudentNote {
  id: string;
  student_id: string;
  enrollment_id: string;
  lesson_id?: string;
  module_id?: string;
  course_id: string;
  note_content: string;
  note_type: 'general' | 'important' | 'question' | 'summary';
  timestamp_in_lesson?: number;
  is_private: boolean;
  tags?: string[];
  tenant_id: number;
  created_at: string;
  updated_at: string;
}

export interface StudentBookmark {
  id: string;
  student_id: string;
  enrollment_id: string;
  lesson_id?: string;
  module_id?: string;
  course_id: string;
  bookmark_type: 'lesson' | 'section' | 'quiz' | 'note';
  title: string;
  description?: string;
  timestamp_in_lesson?: number;
  url_fragment?: string;
  is_favorite: boolean;
  tenant_id: number;
  created_at: string;
  updated_at: string;
}

export interface StudentLearningGoal {
  id: string;
  student_id: string;
  enrollment_id?: string;
  course_id?: string;
  goal_type: 'daily' | 'weekly' | 'monthly' | 'course_completion' | 'skill_mastery';
  title: string;
  description?: string;
  target_value: number;
  current_value: number;
  unit: 'minutes' | 'lessons' | 'modules' | 'quizzes';
  target_date?: string;
  is_completed: boolean;
  is_active: boolean;
  tenant_id: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface StudentStudySchedule {
  id: string;
  student_id: string;
  enrollment_id?: string;
  course_id?: string;
  scheduled_date: string;
  scheduled_time?: string;
  duration_minutes: number;
  activity_type: 'lesson' | 'quiz' | 'review' | 'practice';
  activity_id?: string;
  title: string;
  description?: string;
  is_completed: boolean;
  reminder_sent: boolean;
  tenant_id: number;
  created_at: string;
  updated_at: string;
}

export interface StudentRecommendation {
  id: string;
  student_id: string;
  enrollment_id?: string;
  course_id?: string;
  recommendation_type: 'review' | 'practice' | 'next_lesson' | 'skill_gap';
  title: string;
  description: string;
  priority: 1 | 2 | 3; // 1=haute, 2=moyenne, 3=basse
  action_url?: string;
  is_read: boolean;
  is_dismissed: boolean;
  expires_at?: string;
  tenant_id: number;
  created_at: string;
  updated_at: string;
}

export interface QuizAttemptAnalytics {
  id: string;
  attempt_id: string;
  question_id: string;
  student_answer_id?: string;
  time_spent_seconds: number;
  attempts_count: number;
  feedback_shown?: string;
  explanation?: string;
  difficulty_rating?: number;
  hint_used: boolean;
  tenant_id: number;
  created_at: string;
}

// Services pour les statistiques d'apprentissage
export const startLearningSession = async (
  studentId: string,
  enrollmentId: string,
  courseId: string,
  lessonId?: string,
  moduleId?: string,
  tenantId?: number
): Promise<StudentLearningStats> => {
  const { data, error } = await supabase
    .from('elearn_student_learning_stats')
    .insert([{
      student_id: studentId,
      enrollment_id: enrollmentId,
      course_id: courseId,
      lesson_id: lessonId,
      module_id: moduleId,
      session_start: new Date().toISOString(),
      tenant_id: tenantId
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const endLearningSession = async (
  sessionId: string,
  progressBefore: number,
  progressAfter: number,
  actionsCount: number = 0
): Promise<StudentLearningStats> => {
  const sessionEnd = new Date();
  const { data: session } = await supabase
    .from('elearn_student_learning_stats')
    .select('session_start')
    .eq('id', sessionId)
    .single();

  const timeSpentMinutes = session 
    ? Math.round((sessionEnd.getTime() - new Date(session.session_start).getTime()) / 60000)
    : 0;

  const { data, error } = await supabase
    .from('elearn_student_learning_stats')
    .update({
      session_end: sessionEnd.toISOString(),
      time_spent_minutes: timeSpentMinutes,
      progress_before: progressBefore,
      progress_after: progressAfter,
      actions_count: actionsCount,
      updated_at: new Date().toISOString()
    })
    .eq('id', sessionId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getStudentLearningStats = async (
  studentId: string,
  courseId?: string,
  enrollmentId?: string,
  tenantId?: number,
  dateFrom?: string,
  dateTo?: string
): Promise<StudentLearningStats[]> => {
  let query = supabase
    .from('elearn_student_learning_stats')
    .select('*')
    .eq('student_id', studentId);

  if (tenantId) query = query.eq('tenant_id', tenantId);
  if (courseId) query = query.eq('course_id', courseId);
  if (enrollmentId) query = query.eq('enrollment_id', enrollmentId);
  if (dateFrom) query = query.gte('created_at', dateFrom);
  if (dateTo) query = query.lte('created_at', dateTo);

  query = query.order('created_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// Services pour les notes
export const createStudentNote = async (
  studentId: string,
  enrollmentId: string,
  courseId: string,
  noteContent: string,
  noteType: 'general' | 'important' | 'question' | 'summary' = 'general',
  lessonId?: string,
  moduleId?: string,
  timestampInLesson?: number,
  tags?: string[],
  tenantId?: number
): Promise<StudentNote> => {
  const { data, error } = await supabase
    .from('elearn_student_notes')
    .insert([{
      student_id: studentId,
      enrollment_id: enrollmentId,
      course_id: courseId,
      lesson_id: lessonId,
      module_id: moduleId,
      note_content: noteContent,
      note_type: noteType,
      timestamp_in_lesson: timestampInLesson,
      tags: tags,
      tenant_id: tenantId
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateStudentNote = async (
  noteId: string,
  updates: Partial<Pick<StudentNote, 'note_content' | 'note_type' | 'tags' | 'is_private'>>
): Promise<StudentNote> => {
  const { data, error } = await supabase
    .from('elearn_student_notes')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', noteId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteStudentNote = async (noteId: string): Promise<void> => {
  const { error } = await supabase
    .from('elearn_student_notes')
    .delete()
    .eq('id', noteId);

  if (error) throw error;
};

export const getStudentNotes = async (
  studentId: string,
  courseId?: string,
  lessonId?: string,
  tenantId?: number
): Promise<StudentNote[]> => {
  let query = supabase
    .from('elearn_student_notes')
    .select('*')
    .eq('student_id', studentId);

  if (tenantId) query = query.eq('tenant_id', tenantId);
  if (courseId) query = query.eq('course_id', courseId);
  if (lessonId) query = query.eq('lesson_id', lessonId);

  query = query.order('created_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// Services pour les marque-pages
export const createStudentBookmark = async (
  studentId: string,
  enrollmentId: string,
  courseId: string,
  bookmarkType: 'lesson' | 'section' | 'quiz' | 'note',
  title: string,
  description?: string,
  lessonId?: string,
  moduleId?: string,
  timestampInLesson?: number,
  urlFragment?: string,
  tenantId?: number
): Promise<StudentBookmark> => {
  const { data, error } = await supabase
    .from('elearn_student_bookmarks')
    .insert([{
      student_id: studentId,
      enrollment_id: enrollmentId,
      course_id: courseId,
      lesson_id: lessonId,
      module_id: moduleId,
      bookmark_type: bookmarkType,
      title: title,
      description: description,
      timestamp_in_lesson: timestampInLesson,
      url_fragment: urlFragment,
      tenant_id: tenantId
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const toggleBookmarkFavorite = async (
  bookmarkId: string,
  isFavorite: boolean
): Promise<StudentBookmark> => {
  const { data, error } = await supabase
    .from('elearn_student_bookmarks')
    .update({ is_favorite: isFavorite, updated_at: new Date().toISOString() })
    .eq('id', bookmarkId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteStudentBookmark = async (bookmarkId: string): Promise<void> => {
  const { error } = await supabase
    .from('elearn_student_bookmarks')
    .delete()
    .eq('id', bookmarkId);

  if (error) throw error;
};

export const getStudentBookmarks = async (
  studentId: string,
  courseId?: string,
  bookmarkType?: string,
  favoritesOnly?: boolean,
  tenantId?: number
): Promise<StudentBookmark[]> => {
  let query = supabase
    .from('elearn_student_bookmarks')
    .select('*')
    .eq('student_id', studentId);

  if (tenantId) query = query.eq('tenant_id', tenantId);
  if (courseId) query = query.eq('course_id', courseId);
  if (bookmarkType) query = query.eq('bookmark_type', bookmarkType);
  if (favoritesOnly) query = query.eq('is_favorite', true);

  query = query.order('created_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// Services pour les objectifs d'apprentissage
export const createLearningGoal = async (
  studentId: string,
  goalType: 'daily' | 'weekly' | 'monthly' | 'course_completion' | 'skill_mastery',
  title: string,
  targetValue: number,
  unit: 'minutes' | 'lessons' | 'modules' | 'quizzes',
  description?: string,
  targetDate?: string,
  enrollmentId?: string,
  courseId?: string,
  tenantId?: number
): Promise<StudentLearningGoal> => {
  const { data, error } = await supabase
    .from('elearn_student_learning_goals')
    .insert([{
      student_id: studentId,
      enrollment_id: enrollmentId,
      course_id: courseId,
      goal_type: goalType,
      title: title,
      description: description,
      target_value: targetValue,
      unit: unit,
      target_date: targetDate,
      tenant_id: tenantId
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateLearningGoalProgress = async (
  goalId: string,
  currentValue: number,
  isCompleted?: boolean
): Promise<StudentLearningGoal> => {
  const updates: any = { 
    current_value: currentValue,
    updated_at: new Date().toISOString()
  };

  if (isCompleted !== undefined) {
    updates.is_completed = isCompleted;
    if (isCompleted) {
      updates.completed_at = new Date().toISOString();
    }
  }

  const { data, error } = await supabase
    .from('elearn_student_learning_goals')
    .update(updates)
    .eq('id', goalId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getStudentLearningGoals = async (
  studentId: string,
  activeOnly: boolean = true,
  goalType?: string,
  tenantId?: number
): Promise<StudentLearningGoal[]> => {
  let query = supabase
    .from('elearn_student_learning_goals')
    .select('*')
    .eq('student_id', studentId);

  if (tenantId) query = query.eq('tenant_id', tenantId);
  if (activeOnly) query = query.eq('is_active', true);
  if (goalType) query = query.eq('goal_type', goalType);

  query = query.order('created_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// Services pour le calendrier d'études
export const createStudySchedule = async (
  studentId: string,
  scheduledDate: string,
  activityType: 'lesson' | 'quiz' | 'review' | 'practice',
  title: string,
  durationMinutes: number = 60,
  scheduledTime?: string,
  description?: string,
  activityId?: string,
  enrollmentId?: string,
  courseId?: string,
  tenantId?: number
): Promise<StudentStudySchedule> => {
  const { data, error } = await supabase
    .from('elearn_student_study_schedule')
    .insert([{
      student_id: studentId,
      enrollment_id: enrollmentId,
      course_id: courseId,
      scheduled_date: scheduledDate,
      scheduled_time: scheduledTime,
      duration_minutes: durationMinutes,
      activity_type: activityType,
      activity_id: activityId,
      title: title,
      description: description,
      tenant_id: tenantId
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const markScheduleItemCompleted = async (
  scheduleId: string,
  isCompleted: boolean = true
): Promise<StudentStudySchedule> => {
  const { data, error } = await supabase
    .from('elearn_student_study_schedule')
    .update({ 
      is_completed: isCompleted,
      updated_at: new Date().toISOString()
    })
    .eq('id', scheduleId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getStudentSchedule = async (
  studentId: string,
  dateFrom?: string,
  dateTo?: string,
  tenantId?: number
): Promise<StudentStudySchedule[]> => {
  let query = supabase
    .from('elearn_student_study_schedule')
    .select('*')
    .eq('student_id', studentId);

  if (tenantId) query = query.eq('tenant_id', tenantId);
  if (dateFrom) query = query.gte('scheduled_date', dateFrom);
  if (dateTo) query = query.lte('scheduled_date', dateTo);

  query = query.order('scheduled_date', { ascending: true });

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// Services pour les recommandations
export const createStudentRecommendation = async (
  studentId: string,
  recommendationType: 'review' | 'practice' | 'next_lesson' | 'skill_gap',
  title: string,
  description: string,
  priority: 1 | 2 | 3 = 2,
  actionUrl?: string,
  expiresAt?: string,
  enrollmentId?: string,
  courseId?: string,
  tenantId?: number
): Promise<StudentRecommendation> => {
  const { data, error } = await supabase
    .from('elearn_student_recommendations')
    .insert([{
      student_id: studentId,
      enrollment_id: enrollmentId,
      course_id: courseId,
      recommendation_type: recommendationType,
      title: title,
      description: description,
      priority: priority,
      action_url: actionUrl,
      expires_at: expiresAt,
      tenant_id: tenantId
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const markRecommendationAsRead = async (
  recommendationId: string,
  isRead: boolean = true
): Promise<StudentRecommendation> => {
  const { data, error } = await supabase
    .from('elearn_student_recommendations')
    .update({ 
      is_read: isRead,
      updated_at: new Date().toISOString()
    })
    .eq('id', recommendationId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const dismissRecommendation = async (
  recommendationId: string
): Promise<StudentRecommendation> => {
  const { data, error } = await supabase
    .from('elearn_student_recommendations')
    .update({ 
      is_dismissed: true,
      updated_at: new Date().toISOString()
    })
    .eq('id', recommendationId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getStudentRecommendations = async (
  studentId: string,
  activeOnly: boolean = true,
  tenantId?: number
): Promise<StudentRecommendation[]> => {
  let query = supabase
    .from('elearn_student_recommendations')
    .select('*')
    .eq('student_id', studentId);

  if (tenantId) query = query.eq('tenant_id', tenantId);
  if (activeOnly) {
    query = query.eq('is_dismissed', false);
    // Filtrer les recommandations expirées
    query = query.or('expires_at.is.null,expires_at.gt.' + new Date().toISOString());
  }

  query = query.order('priority', { ascending: true })
           .order('created_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// Services pour l'analyse des quiz
export const createQuizAttemptAnalytics = async (
  attemptId: string,
  questionId: string,
  timeSpentSeconds: number,
  feedbackShown?: string,
  explanation?: string,
  difficultyRating?: number,
  hintUsed: boolean = false,
  studentAnswerId?: string,
  tenantId?: number
): Promise<QuizAttemptAnalytics> => {
  const { data, error } = await supabase
    .from('elearn_quiz_attempt_analytics')
    .insert([{
      attempt_id: attemptId,
      question_id: questionId,
      student_answer_id: studentAnswerId,
      time_spent_seconds: timeSpentSeconds,
      feedback_shown: feedbackShown,
      explanation: explanation,
      difficulty_rating: difficultyRating,
      hint_used: hintUsed,
      tenant_id: tenantId
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getQuizAttemptAnalytics = async (
  attemptId: string,
  tenantId?: number
): Promise<QuizAttemptAnalytics[]> => {
  let query = supabase
    .from('elearn_quiz_attempt_analytics')
    .select('*')
    .eq('attempt_id', attemptId);

  if (tenantId) query = query.eq('tenant_id', tenantId);

  query = query.order('created_at', { ascending: true });

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

// Fonction utilitaire pour calculer les statistiques d'apprentissage
export const calculateLearningStatistics = async (
  studentId: string,
  courseId?: string,
  tenantId?: number,
  dateFrom?: string,
  dateTo?: string
) => {
  const stats = await getStudentLearningStats(studentId, courseId, undefined, tenantId, dateFrom, dateTo);
  
  const totalTimeMinutes = stats.reduce((sum, stat) => sum + stat.time_spent_minutes, 0);
  const totalSessions = stats.length;
  const averageSessionTime = totalSessions > 0 ? Math.round(totalTimeMinutes / totalSessions) : 0;
  const totalActions = stats.reduce((sum, stat) => sum + stat.actions_count, 0);
  
  // Calculer la progression moyenne
  const progressStats = stats.filter(stat => stat.progress_after > stat.progress_before);
  const averageProgressGain = progressStats.length > 0 
    ? Math.round(progressStats.reduce((sum, stat) => sum + (stat.progress_after - stat.progress_before), 0) / progressStats.length)
    : 0;

  // Grouper par jour pour les statistiques quotidiennes
  const dailyStats = stats.reduce((acc, stat) => {
    const date = stat.created_at.split('T')[0];
    if (!acc[date]) {
      acc[date] = { date, timeMinutes: 0, sessions: 0, actions: 0 };
    }
    acc[date].timeMinutes += stat.time_spent_minutes;
    acc[date].sessions += 1;
    acc[date].actions += stat.actions_count;
    return acc;
  }, {} as Record<string, any>);

  return {
    totalTimeMinutes,
    totalSessions,
    averageSessionTime,
    totalActions,
    averageProgressGain,
    dailyStats: Object.values(dailyStats),
    recentSessions: stats.slice(0, 10) // 10 dernières sessions
  };
};
